import React from "react";
import { CheckCircle2, Circle, ChevronDown, Lock, RotateCcw } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { CourseNodeWithStatus, NodeStatus, CourseNodeType } from "@/types/courses";

const typeLabelStyles: Record<CourseNodeType, string> = {
  lesson: "text-info",
  practice: "text-warning",
  review: "text-correct",
};

interface SequenceRowProps {
  node: CourseNodeWithStatus;
  index: number;
  stepCount: number;
  isOpen: boolean;
  onToggle: () => void;
  onNodeAction: (nodeId: string) => void;
}

function StatusDot({ status }: { status: NodeStatus }) {
  if (status === "completed") {
    return <CheckCircle2 className="h-3.5 w-3.5 text-correct shrink-0" />;
  }
  if (status === "in_progress") {
    return (
      <span className="relative flex h-3.5 w-3.5 shrink-0">
        <span className="absolute inset-0 rounded-full bg-info" />
      </span>
    );
  }
  return <Circle className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />;
}

function typeLabel(type: string) {
  return type.toUpperCase();
}

function actionLabel(status: NodeStatus) {
  switch (status) {
    case "completed":
      return "Review";
    case "in_progress":
      return "Continue";
    default:
      return "Start";
  }
}

export const SequenceRow: React.FC<SequenceRowProps> = ({
  node,
  index,
  stepCount,
  isOpen,
  onToggle,
  onNodeAction,
}) => {
  const isLocked = node.status === "locked";
  const padded = String(index + 1).padStart(2, "0");

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
          <button
          className="flex w-full items-center gap-4 px-4 py-3.5 text-left hover:bg-muted/40 hover:border-l-2 hover:border-l-primary/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          type="button"
        >
          {/* Index */}
          <span className="w-7 text-sm tabular-nums font-mono text-muted-foreground shrink-0">
            {padded}
          </span>

          {/* Title + meta */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold truncate">{node.title}</span>
              <span className={cn("text-xs font-semibold tracking-wider shrink-0", typeLabelStyles[node.type as CourseNodeType] || "text-muted-foreground")}>
                {typeLabel(node.type)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {stepCount} {stepCount === 1 ? "step" : "steps"} · {node.xpReward} XP
            </p>
            {node.status === "in_progress" && (
              <Progress value={node.progress * 100} className="h-1 mt-1.5" />
            )}
          </div>

          {/* Status */}
          <StatusDot status={node.status} />

          {/* Chevron */}
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform shrink-0",
              isOpen && "rotate-180",
            )}
          />
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-4 pb-4 pl-[calc(1rem+1.75rem+1rem)]">
          {node.description && (
            <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
              {node.description}
            </p>
          )}

          {isLocked ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" disabled className="gap-2 opacity-60">
                    <Lock className="h-3.5 w-3.5" />
                    Locked
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Complete prerequisite lessons to unlock</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <div className="flex items-center gap-3">
              {node.status === "completed" ? (
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onNodeAction(node.id)}>
                  <RotateCcw className="h-3 w-3" />
                  Review
                </Button>
              ) : (
                <Button size="sm" onClick={() => onNodeAction(node.id)}>
                  {actionLabel(node.status)}
                </Button>
              )}
              <span className="text-xs text-muted-foreground">{node.xpReward} XP</span>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
